import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, BookOpen, Store, Loader2, Check } from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Catalog detail page — /catalog/:catalogId
 * Shows every active listing tagged with this catalog, grouped by shop,
 * so buyers can browse "what's inside" a given seasonal catalog.
 */
export default function CatalogDetailPage() {
  const { catalogId } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [myListings, setMyListings] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const loadCatalog = async () => {
    try {
      const res = await axios.get(`${API}/catalog/listings/${catalogId}`);
      setData(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await loadCatalog();
      if (cancelled) { setLoading(false); return; }
      // Verified shop owner → load their listings for the manage panel.
      try {
        const acc = await axios.get(`${API}/catalog/my-access`);
        if (!cancelled && acc.data?.is_verified) {
          setVerified(true);
          const mine = await axios.get(`${API}/catalog/my-listings`);
          if (!cancelled) setMyListings(mine.data || []);
        }
      } catch (_) { /* not logged in / no shop — hide panel */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [catalogId]);

  const toggleMembership = async (listingId, member) => {
    setSavingId(listingId);
    try {
      await axios.post(`${API}/catalog/listing-membership`, { listing_id: listingId, catalog_id: catalogId, member });
      setMyListings(prev => prev.map(l => l.id === listingId
        ? { ...l, catalogs: member ? [...new Set([...(l.catalogs || []), catalogId])] : (l.catalogs || []).filter(c => c !== catalogId) }
        : l));
      await loadCatalog();
      toast.success(member
        ? (language === 'lv' ? 'Pievienots katalogam' : language === 'ru' ? 'Добавлено в каталог' : 'Added to catalog')
        : (language === 'lv' ? 'Izņemts no kataloga' : language === 'ru' ? 'Убрано из каталога' : 'Removed from catalog'));
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'lv' ? 'Neizdevās saglabāt' : language === 'ru' ? 'Не удалось сохранить' : 'Could not save'));
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]" />
      </div>
    );
  }

  const catalogName = data?.catalog_name?.[language] || data?.catalog_name?.lv || catalogId;
  const listings = data?.listings || [];

  // Group listings by shop for the grouped view.
  const byShop = listings.reduce((acc, l) => {
    const key = l.owner_id || 'unknown';
    if (!acc[key]) acc[key] = { shop_name: l.shop_name, shop_logo: l.shop_logo, owner_id: l.owner_id, items: [] };
    acc[key].items.push(l);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="catalog-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/catalog')}
          className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6"
          data-testid="catalog-detail-back"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'lv' ? 'Atpakaļ uz katalogiem' : language === 'ru' ? 'Назад к каталогам' : 'Back to catalogs'}
        </button>

        <div className="flex items-start gap-4 mb-10">
          <div className="w-14 h-14 rounded-2xl bg-[#D97736]/10 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-7 h-7 text-[#D97736]" />
          </div>
          <div>
            <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-[#121212]">{catalogName}</h1>
            <p className="text-[#5C5C5C] mt-1">
              {listings.length === 0
                ? (language === 'lv' ? 'Šajā katalogā vēl nav neviena sludinājuma.' : language === 'ru' ? 'В этом каталоге пока нет объявлений.' : 'No listings in this catalog yet.')
                : (language === 'lv'
                  ? `${listings.length} sludinājumi no ${Object.keys(byShop).length} veikaliem`
                  : language === 'ru'
                  ? `${listings.length} объявлений от ${Object.keys(byShop).length} магазинов`
                  : `${listings.length} listings from ${Object.keys(byShop).length} shops`)}
            </p>
          </div>
        </div>

        {/* Verified shop owner — manage which of my products are in this catalog */}
        {verified && myListings.length > 0 && (
          <details className="bg-white border border-[#4A6B53]/30 rounded-2xl mb-8 group" data-testid="catalog-manage-panel" open>
            <summary className="flex items-center justify-between gap-2 p-5 cursor-pointer list-none">
              <div className="flex items-center gap-2">
                <Store className="w-5 h-5 text-[#4A6B53]" />
                <span className="font-medium text-[#121212]">
                  {language === 'lv' ? 'Pārvaldi savus produktus šajā katalogā' : language === 'ru' ? 'Управляйте своими товарами в этом каталоге' : 'Manage your products in this catalog'}
                </span>
                <span className="text-xs text-white bg-[#4A6B53] px-2 py-0.5 rounded-full">
                  {language === 'lv' ? 'Bezmaksas' : language === 'ru' ? 'Бесплатно' : 'Free'}
                </span>
              </div>
            </summary>
            <div className="px-5 pb-5 space-y-2 max-h-96 overflow-y-auto">
              {myListings.map(l => {
                const inCatalog = (l.catalogs || []).includes(catalogId);
                return (
                  <div key={l.id} className="flex items-center gap-3 p-2 rounded-xl border border-[#E5E4E0]" data-testid={`manage-listing-${l.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-[#F3F2EE] overflow-hidden flex items-center justify-center flex-shrink-0">
                      {l.image ? <img src={resolveAssetUrl(l.image)} alt={l.title} className="w-full h-full object-cover" /> : <Store className="w-4 h-4 text-[#8A8A8A]" />}
                    </div>
                    <span className="flex-1 text-sm text-[#121212] line-clamp-1">{l.title}</span>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => toggleMembership(l.id, !inCatalog)}
                      disabled={savingId === l.id}
                      className={inCatalog
                        ? 'bg-[#4A6B53] hover:bg-[#3a5642] text-white'
                        : 'bg-white border border-[#D97736] text-[#D97736] hover:bg-[#FFF8F0]'}
                      data-testid={`manage-toggle-${l.id}`}
                    >
                      {savingId === l.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : inCatalog
                          ? <><Check className="w-4 h-4 mr-1" />{language === 'lv' ? 'Katalogā' : language === 'ru' ? 'В каталоге' : 'In catalog'}</>
                          : (language === 'lv' ? 'Pievienot' : language === 'ru' ? 'Добавить' : 'Add')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </details>
        )}

        {listings.length === 0 ? (
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-10 text-center" data-testid="catalog-detail-empty">
            <Store className="w-12 h-12 text-[#D97736]/40 mx-auto mb-3" />
            <p className="text-[#5C5C5C] mb-4">
              {language === 'lv'
                ? 'Esi pirmais, kas pievieno sludinājumu šim katalogam!'
                : language === 'ru'
                ? 'Будьте первым, кто добавит объявление в этот каталог!'
                : 'Be the first to add a listing to this catalog!'}
            </p>
            <Button
              onClick={() => navigate(verified ? '/create-listing' : '/shop-onboarding')}
              className="bg-[#D97736] hover:bg-[#C06528] text-white"
              data-testid="catalog-detail-join"
            >
              {verified
                ? (language === 'lv' ? 'Pievienot sludinājumu' : language === 'ru' ? 'Добавить объявление' : 'Add a listing')
                : (language === 'lv' ? 'Verificē veikalu, lai pievienotu' : language === 'ru' ? 'Пройдите верификацию, чтобы добавить' : 'Verify shop to add')}
            </Button>
          </div>
        ) : (
          <div className="space-y-10">
            {Object.values(byShop).map(group => (
              <section key={group.owner_id} className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid={`catalog-shop-group-${group.owner_id}`}>
                <Link
                  to={`/shop/${group.owner_id}`}
                  className="flex items-center gap-3 mb-5 hover:opacity-80 transition-opacity"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#FAFAFA] border border-[#E5E4E0] overflow-hidden flex items-center justify-center text-[#D97736] font-bold flex-shrink-0">
                    {group.shop_logo ? (
                      <img src={resolveAssetUrl(group.shop_logo)} alt={group.shop_name} className="w-full h-full object-cover" />
                    ) : (
                      (group.shop_name || '?').charAt(0).toUpperCase()
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#121212]">{group.shop_name || (language === 'lv' ? 'Veikals' : language === 'ru' ? 'Магазин' : 'Shop')}</h3>
                    <p className="text-xs text-[#8A8A8A]">{group.items.length} {language === 'lv' ? 'sludinājumi' : language === 'ru' ? 'объявлений' : 'listings'}</p>
                  </div>
                </Link>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {group.items.map(l => (
                    <Link
                      key={l.id}
                      to={`/listing/${l.slug || l.id}`}
                      className="block bg-[#FAFAFA] rounded-xl overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all"
                      data-testid={`catalog-listing-${l.id}`}
                    >
                      <div className="aspect-square bg-[#F3F2EE] overflow-hidden">
                        {l.image ? (
                          <img src={resolveAssetUrl(l.image)} alt={l.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#8A8A8A]">
                            <Store className="w-6 h-6" />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-sm text-[#121212] font-medium line-clamp-2 mb-1">{l.title}</p>
                        {l.price != null && (
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge className="bg-[#D97736]/10 text-[#D97736] border-0 text-xs">€{l.price}</Badge>
                            {l.price_type === 'negotiable' && (
                              <span className="text-[10px] text-[#8A8A8A]">
                                {language === 'lv' ? 'pēc vienošanās' : language === 'ru' ? 'по договорённости' : 'negotiable'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
